import axios from "axios";
import API_BASE_URL from "../config/apiConfig";

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

const authHeader = (token) => (token ? { Authorization: `Bearer ${token}` } : {});
const deviceHeader = (deviceToken) => (deviceToken ? { "X-Lead-Device-Token": deviceToken } : {});

export const loginLeadTeam = async (team, username, password, device = {}) => {
  const path = team === "CALL" ? "/api/call-team/auth/login" : "/api/chat-team/auth/login";
  const { data } = await client.post(path, { username, password, ...device });
  return data;
};

export const validateLeadTeam = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/auth/validate" : "/api/chat-team/auth/validate";
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

export const listTeamLanguages = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/languages" : "/api/chat-team/languages";
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

export const listCallTeamMembers = async (token) => {
  const { data } = await client.get("/api/call-team/team-members", { headers: authHeader(token) });
  return data;
};

export const listTeamConversionMembers = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/conversion-members" : "/api/chat-team/conversion-members";
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

// The member's own incentive figures. The server reads who they are from the token,
// so there is no way to ask for somebody else's.
export const getMyIncentiveHeadline = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/incentives/me" : "/api/chat-team/incentives/me";
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

export const getMyIncentiveMonth = async (team, token, period) => {
  const base = team === "CALL" ? "/api/call-team/incentives/me" : "/api/chat-team/incentives/me";
  const { data } = await client.get(`${base}/months/${period}`, { headers: authHeader(token) });
  return data;
};

export const listTeamDailyUpdateSuggestions = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/daily-update-suggestions" : "/api/chat-team/daily-update-suggestions";
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

export const getCurrentTeamDay = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/auth/session" : "/api/chat-team/auth/session";
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

export const getTeamWorkStatus = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/auth/work-status" : "/api/chat-team/auth/work-status";
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

export const startTeamDay = async (team, token, deviceToken = "") => {
  const path = team === "CALL" ? "/api/call-team/day/start" : "/api/chat-team/day/start";
  const { data } = await client.post(path, {}, { headers: { ...authHeader(token), ...deviceHeader(deviceToken) } });
  return data;
};

export const endTeamDay = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/day/end" : "/api/chat-team/day/end";
  const { data } = await client.post(path, {}, { headers: authHeader(token) });
  return data;
};

export const listTeamAttendanceRequests = async (team, token, params = {}) => {
  const path = team === "CALL" ? "/api/call-team/attendance-requests" : "/api/chat-team/attendance-requests";
  const { data } = await client.get(path, { params, headers: authHeader(token) });
  return data;
};

export const createTeamAttendanceRequest = async (team, token, payload) => {
  const path = team === "CALL" ? "/api/call-team/attendance-requests" : "/api/chat-team/attendance-requests";
  const { data } = await client.post(path, payload, { headers: authHeader(token) });
  return data;
};

export const cancelTeamAttendanceRequest = async (team, token, id) => {
  const path = team === "CALL" ? `/api/call-team/attendance-requests/${id}/cancel` : `/api/chat-team/attendance-requests/${id}/cancel`;
  const { data } = await client.post(path, {}, { headers: authHeader(token) });
  return data;
};

export const listTeamLeads = async (team, token, all = false, params = {}) => {
  const path = team === "CALL" ? "/api/call-team/leads" : "/api/chat-team/leads";
  const requestParams = { ...params };
  if (all) requestParams.all = true;
  const { data } = await client.get(path, { params: requestParams, headers: authHeader(token) });
  return data;
};

// Server-paginated listing used by the team dashboards. listTeamLeads stays for callers that still want every row.
export const listTeamLeadsPage = async (team, token, params = {}) => {
  const path = team === "CALL" ? "/api/call-team/leads/page" : "/api/chat-team/leads/page";
  const { data } = await client.get(path, {
    params,
    paramsSerializer: { indexes: null },
    headers: authHeader(token),
  });
  return data;
};

// One request for a burst of live updates instead of one /sync call per changed lead.
export const syncTeamLeads = async (team, token, ids = []) => {
  if (!ids.length) return [];
  const path = team === "CALL" ? "/api/call-team/leads/sync" : "/api/chat-team/leads/sync";
  const { data } = await client.get(path, {
    params: { ids },
    paramsSerializer: { indexes: null },
    headers: authHeader(token),
  });
  return data;
};

export const clickToCall = async (token, extension, phoneNumber, leadId = null) => {
  const { data } = await client.post("/api/call-team/click-to-call", { extension, phoneNumber, leadId }, {
    headers: authHeader(token),
  });
  return data;
};

// Extension calls with numbers no lead has: the member's own, read from the token.
export const getMyUnlinkedCalls = async (token) => {
  const { data } = await client.get("/api/call-team/unlinked-calls", { headers: authHeader(token) });
  return data;
};

export const searchLeadsForCallLink = async (token, q) => {
  const { data } = await client.get("/api/call-team/unlinked-calls/lead-options", { params: { q }, headers: authHeader(token) });
  return data;
};

// Answers 409 when the lead already has a different alternate number; resend with replaceAlternate.
export const linkUnlinkedNumber = async (token, customerNumber, leadId, replaceAlternate = false) => {
  const { data } = await client.post("/api/call-team/unlinked-calls/link", { customerNumber, leadId, replaceAlternate }, {
    headers: authHeader(token),
  });
  return data;
};

export const markNotCustomer = async (token, customerNumber, category, note = "") => {
  await client.put("/api/call-team/unlinked-calls/not-customer", { customerNumber, category, note }, { headers: authHeader(token) });
};

export const unmarkNotCustomer = async (token, customerNumber) => {
  await client.delete("/api/call-team/unlinked-calls/not-customer", { params: { customerNumber }, headers: authHeader(token) });
};

export const createTeamLead = async (team, token, payload) => {
  const path = team === "CALL" ? "/api/call-team/leads" : "/api/chat-team/leads";
  const { data } = await client.post(path, payload, { headers: authHeader(token) });
  return data;
};

export const updateTeamLead = async (team, token, id, payload) => {
  const path = team === "CALL" ? `/api/call-team/leads/${id}` : `/api/chat-team/leads/${id}`;
  const { data } = await client.patch(path, payload, { headers: authHeader(token) });
  return data;
};

export const assignCallTeamLead = async (token, id, assignedCallMemberId) => {
  const { data } = await client.patch(`/api/call-team/leads/${id}/assignment`, { assignedCallMemberId }, {
    headers: authHeader(token),
  });
  return data;
};

export const updateLeadCallNeeded = async (team, token, id, payload) => {
  const path = team === "CALL" ? `/api/call-team/leads/${id}/call-needed` : `/api/chat-team/leads/${id}/call-needed`;
  const { data } = await client.patch(path, payload, { headers: authHeader(token) });
  return data;
};

// Call Needed leads with a customer-requested time; every logged-in Call Team member is reminded.
export const getCallNeededReminders = async (token) => {
  const { data } = await client.get("/api/call-team/call-needed-reminders", { headers: authHeader(token) });
  return data;
};

export const markManualCallOutcome = async (token, id, payload) => {
  const { data } = await client.post(`/api/call-team/leads/${id}/manual-call`, payload, {
    headers: authHeader(token),
  });
  return data;
};

export const listLeadBookingOptions = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/booking-options" : "/api/chat-team/booking-options";
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

export const convertLeadToBooking = async (team, token, id, payload) => {
  const path = team === "CALL" ? `/api/call-team/leads/${id}/convert-to-booking` : `/api/chat-team/leads/${id}/convert-to-booking`;
  const { data } = await client.post(path, payload, { headers: authHeader(token) });
  return data;
};
export const createLeadBooking = async (team, token, payload) => {
  const path = team === "CALL" ? "/api/call-team/leads/convert-to-booking" : "/api/chat-team/leads/convert-to-booking";
  const { data } = await client.post(path, payload, { headers: authHeader(token) });
  return data;
};
export const getCurrentTeamBreak = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/breaks/current" : "/api/chat-team/breaks/current";
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

export const startTeamBreak = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/breaks/start" : "/api/chat-team/breaks/start";
  const { data } = await client.post(path, {}, { headers: authHeader(token) });
  return data;
};

export const endTeamBreak = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/breaks/end" : "/api/chat-team/breaks/end";
  const { data } = await client.post(path, {}, { headers: authHeader(token) });
  return data;
};

export const getTeamBreaksToday = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/breaks/today" : "/api/chat-team/breaks/today";
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

export const getCurrentLeadWork = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/lead-work/current" : "/api/chat-team/lead-work/current";
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

export const getTeamLeadWorkToday = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/lead-work/today" : "/api/chat-team/lead-work/today";
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

// Same as getTeamLeadWorkToday, but one row per actual session instead of merged per lead - use for
// the My Insights lead-work timeline (idle-time gaps and session timing).
export const getTeamLeadWorkSessionsToday = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/lead-work/sessions/today" : "/api/chat-team/lead-work/sessions/today";
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

export const getTeamLeadChanges = async (team, token, since) => {
  const path = team === "CALL" ? "/api/call-team/lead-changes" : "/api/chat-team/lead-changes";
  const { data } = await client.get(path, {
    params: since ? { since } : {},
    headers: authHeader(token),
  });
  return data;
};
export const syncTeamLead = async (team, token, leadId) => {
  const path = team === "CALL" ? `/api/call-team/leads/${leadId}/sync` : `/api/chat-team/leads/${leadId}/sync`;
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

export const getTeamLeadHistory = async (team, token, leadId) => {
  const path = team === "CALL" ? `/api/call-team/leads/${leadId}/history` : `/api/chat-team/leads/${leadId}/history`;
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

export const getTeamLeadCalls = async (team, token, leadId) => {
  const path = team === "CALL" ? `/api/call-team/leads/${leadId}/calls` : `/api/chat-team/leads/${leadId}/calls`;
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

export const connectLeadEvents = async (team, token, signal, onLeadUpdated) => {
  const path = team === "CALL" ? "/api/call-team/lead-events" : "/api/chat-team/lead-events";
  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: authHeader(token),
    signal,
  });
  if (!response.ok || !response.body) throw new Error("Could not connect to lead updates");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (!signal.aborted) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
    let boundary;
    while ((boundary = buffer.indexOf("\n\n")) >= 0) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const event = block.split("\n").find((line) => line.startsWith("event:"))?.slice(6).trim();
      const data = block.split("\n").find((line) => line.startsWith("data:"))?.slice(5).trim();
      if (event === "lead-updated" && data) onLeadUpdated(Number(data));
    }
  }
};
export const startLeadWork = async (team, token, leadId) => {
  const path = team === "CALL" ? `/api/call-team/leads/${leadId}/work/start` : `/api/chat-team/leads/${leadId}/work/start`;
  const { data } = await client.post(path, {}, { headers: authHeader(token) });
  return data;
};

export const endLeadWork = async (team, token, leadId) => {
  const path = team === "CALL" ? `/api/call-team/leads/${leadId}/work/end` : `/api/chat-team/leads/${leadId}/work/end`;
  const { data } = await client.post(path, {}, { headers: authHeader(token) });
  return data;
};

// Creates or updates the caller's own flag. remindAt is "YYYY-MM-DDTHH:mm" (IST) or empty for no reminder.
export const flagLead = async (team, token, leadId, note, remindAt) => {
  const path = team === "CALL" ? `/api/call-team/leads/${leadId}/flag` : `/api/chat-team/leads/${leadId}/flag`;
  const { data } = await client.post(path, { note: note || "", remindAt: remindAt || null }, { headers: authHeader(token) });
  return data;
};

// The caller's own flags with reminders, soonest first.
export const getMyFlagReminders = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/flags/mine" : "/api/chat-team/flags/mine";
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

export const unflagLead = async (team, token, leadId) => {
  const path = team === "CALL" ? `/api/call-team/leads/${leadId}/unflag` : `/api/chat-team/leads/${leadId}/unflag`;
  const { data } = await client.post(path, {}, { headers: authHeader(token) });
  return data;
};

const adminToken = () => localStorage.getItem("adminToken");

// Every employee's lead flags and follow-up reminders, soonest first.
export const listAdminLeadFlags = async () => {
  const { data } = await client.get("/api/admin/lead-tracker/flags", { headers: authHeader(adminToken()) });
  return data;
};

export const listAdminLeadMembers = async (team) => {
  const { data } = await client.get("/api/admin/lead-tracker/team-members", {
    params: team ? { team } : {},
    headers: authHeader(adminToken()),
  });
  return data;
};

export const listAdminLeadLanguages = async () => {
  const { data } = await client.get("/api/admin/lead-tracker/languages", {
    headers: authHeader(adminToken()),
  });
  return data;
};

export const saveAdminLeadLanguage = async (language) => {
  const path = language.id ? `/api/admin/lead-tracker/languages/${language.id}` : "/api/admin/lead-tracker/languages";
  const method = language.id ? "put" : "post";
  const { data } = await client[method](path, language, { headers: authHeader(adminToken()) });
  return data;
};

export const listAdminDailyUpdateSuggestions = async () => {
  const { data } = await client.get("/api/admin/lead-tracker/daily-update-suggestions", {
    headers: authHeader(adminToken()),
  });
  return data;
};

export const saveAdminDailyUpdateSuggestion = async (suggestion) => {
  const path = suggestion.id ? `/api/admin/lead-tracker/daily-update-suggestions/${suggestion.id}` : "/api/admin/lead-tracker/daily-update-suggestions";
  const method = suggestion.id ? "put" : "post";
  const { data } = await client[method](path, suggestion, { headers: authHeader(adminToken()) });
  return data;
};

export const deleteAdminDailyUpdateSuggestion = async (id) => {
  await client.delete(`/api/admin/lead-tracker/daily-update-suggestions/${id}`, {
    headers: authHeader(adminToken()),
  });
};

export const saveAdminLeadMember = async (member) => {
  const token = adminToken();
  if (member.id) {
    const { data } = await client.patch(`/api/admin/lead-tracker/team-members/${member.id}`, member, {
      headers: authHeader(token),
    });
    return data;
  }
  const { data } = await client.post("/api/admin/lead-tracker/team-members", member, {
    headers: authHeader(token),
  });
  return data;
};

export const listAdminLeadMemberDevices = async (memberId) => {
  const { data } = await client.get(`/api/admin/lead-tracker/team-members/${memberId}/devices`, {
    headers: authHeader(adminToken()),
  });
  return data;
};

export const approveAdminLeadMemberDevice = async (deviceId) => {
  const { data } = await client.post(`/api/admin/lead-tracker/team-member-devices/${deviceId}/approve`, {}, {
    headers: authHeader(adminToken()),
  });
  return data;
};

export const blockAdminLeadMemberDevice = async (deviceId) => {
  const { data } = await client.post(`/api/admin/lead-tracker/team-member-devices/${deviceId}/block`, {}, {
    headers: authHeader(adminToken()),
  });
  return data;
};

export const forceLogoutAdminLeadMember = async (memberId) => {
  const { data } = await client.post(`/api/admin/lead-tracker/team-members/${memberId}/force-logout`, {}, {
    headers: authHeader(adminToken()),
  });
  return data;
};

export const listAdminLeads = async (team) => {
  const { data } = await client.get("/api/admin/lead-tracker/leads", {
    params: team ? { team } : {},
    headers: authHeader(adminToken()),
  });
  return data;
};

export const getAdminLead = async (id) => {
  const { data } = await client.get(`/api/admin/lead-tracker/leads/${id}`, {
    headers: authHeader(adminToken()),
  });
  return data;
};

export const deleteAdminLead = async (id) => {
  await client.delete(`/api/admin/lead-tracker/leads/${id}`, {
    headers: authHeader(adminToken()),
  });
};

export const listAdminTeamLeads = async () => {
  const { data } = await client.get("/api/admin/lead-tracker/admin-team/leads", {
    headers: authHeader(adminToken()),
  });
  return data;
};

export const listAdminTeamInterestStatuses = async () => {
  const { data } = await client.get("/api/admin/lead-tracker/admin-team/interest-statuses", {
    headers: authHeader(adminToken()),
  });
  return data;
};

export const listAdminBreaks = async (team, timeFrame, filters = {}) => {
  const params = { ...(team ? { team } : {}), ...(timeFrame ? { timeFrame } : {}), ...filters };
  const { data } = await client.get("/api/admin/lead-tracker/breaks", {
    params,
    headers: authHeader(adminToken()),
  });
  return data;
};

export const endAdminBreak = async (id) => {
  const { data } = await client.post(`/api/admin/lead-tracker/breaks/${id}/end`, {}, {
    headers: authHeader(adminToken()),
  });
  return data;
};

export const listAdminLoginSessions = async (team, timeFrame, filters = {}) => {
  const params = { ...(team ? { team } : {}), ...(timeFrame ? { timeFrame } : {}), ...filters };
  const { data } = await client.get("/api/admin/lead-tracker/login-sessions", {
    params,
    headers: authHeader(adminToken()),
  });
  return data;
};

export const endAdminLoginSession = async (id) => {
  const { data } = await client.post(`/api/admin/lead-tracker/login-sessions/${id}/end`, {}, {
    headers: authHeader(adminToken()),
  });
  return data;
};

export const listAdminAttendanceRequests = async (params = {}) => {
  const { data } = await client.get("/api/admin/lead-tracker/attendance-requests", {
    params,
    headers: authHeader(adminToken()),
  });
  return data;
};

export const createAdminAttendanceRequest = async (payload) => {
  const { data } = await client.post("/api/admin/lead-tracker/attendance-requests", payload, {
    headers: authHeader(adminToken()),
  });
  return data;
};

export const updateAdminAttendanceRequestStatus = async (id, payload) => {
  const { data } = await client.patch(`/api/admin/lead-tracker/attendance-requests/${id}/status`, payload, {
    headers: authHeader(adminToken()),
  });
  return data;
};

export const listAdminAttendanceMonthly = async (params = {}) => {
  const { data } = await client.get("/api/admin/lead-tracker/attendance-monthly", {
    params,
    headers: authHeader(adminToken()),
  });
  return data;
};

export const listAdminLeadWork = async (team, timeFrame, filters = {}) => {
  const params = { ...(team ? { team } : {}), ...(timeFrame ? { timeFrame } : {}), ...filters };
  const { data } = await client.get("/api/admin/lead-tracker/lead-work", {
    params,
    headers: authHeader(adminToken()),
  });
  return data;
};

// Same filters as listAdminLeadWork, but one row per actual session instead of merged per lead - use
// for the lead-work timeline (idle-time gaps and session timing), not for per-lead totals.
export const listAdminLeadWorkSessions = async (team, timeFrame, filters = {}) => {
  const params = { ...(team ? { team } : {}), ...(timeFrame ? { timeFrame } : {}), ...filters };
  const { data } = await client.get("/api/admin/lead-tracker/lead-work/sessions", {
    params,
    headers: authHeader(adminToken()),
  });
  return data;
};

export const updateAdminLead = async (id, payload) => {
  const { data } = await client.patch(`/api/admin/lead-tracker/leads/${id}`, payload, {
    headers: authHeader(adminToken()),
  });
  return data;
};

export const updateAdminTeamLead = async (id, payload) => {
  const { data } = await client.patch(`/api/admin/lead-tracker/admin-team/leads/${id}`, payload, {
    headers: authHeader(adminToken()),
  });
  return data;
};

export const listAdminLeadBookingOptions = async () => {
  const { data } = await client.get("/api/admin/lead-tracker/booking-options", {
    headers: authHeader(adminToken()),
  });
  return data;
};

export const convertAdminTeamLeadToBooking = async (id, payload) => {
  const { data } = await client.post(`/api/admin/lead-tracker/admin-team/leads/${id}/convert-to-booking`, payload, {
    headers: authHeader(adminToken()),
  });
  return data;
};

// The itinerary plan picked on the confirmation form, from either the team portal or the admin
// Lead Tracker. It is stored at once and joins the booking when the confirmation is saved.
export const uploadLeadItinerary = async (team, file) => {
  const form = new FormData();
  form.append("file", file);
  const path = team === "ADMIN"
    ? "/api/admin/lead-tracker/booking-documents"
    : team === "CALL" ? "/api/call-team/booking-documents" : "/api/chat-team/booking-documents";
  const token = team === "ADMIN"
    ? adminToken()
    : localStorage.getItem(team === "CALL" ? "callTeamToken" : "chatTeamToken");
  const { data } = await client.post(path, form, {
    headers: { ...authHeader(token), "Content-Type": "multipart/form-data" },
  });
  return data;
};
export const getAdminLeadHistory = async (id) => {
  const { data } = await client.get(`/api/admin/lead-tracker/leads/${id}/history`, {
    headers: authHeader(adminToken()),
  });
  return data;
};
export const getAdminLeadCalls = async (id) => {
  const { data } = await client.get(`/api/admin/lead-tracker/leads/${id}/calls`, {
    headers: authHeader(adminToken()),
  });
  return data;
};

export const getAdminUnlinkedCalls = async (timeFrame, filters = {}) => {
  const { data } = await client.get("/api/admin/lead-tracker/unlinked-calls", {
    params: { ...(timeFrame ? { timeFrame } : {}), ...filters },
    headers: authHeader(adminToken()),
  });
  return data;
};

export const getLeadDashboard = async (team, timeFrame, filters = {}) => {
  const params = { ...(team ? { team } : {}), ...(timeFrame ? { timeFrame } : {}), ...filters };
  const { data } = await client.get("/api/admin/lead-tracker/dashboard", {
    params,
    headers: authHeader(adminToken()),
  });
  return data;
};

export const getDashboardMetricLeads = async (metric, team, timeFrame, filters = {}) => {
  const params = { metric, ...(team ? { team } : {}), ...(timeFrame ? { timeFrame } : {}), ...filters };
  const { data } = await client.get("/api/admin/lead-tracker/dashboard/leads", {
    params,
    headers: authHeader(adminToken()),
  });
  return data;
};

export const refreshLeadFollowUps = async () => {
  const { data } = await client.post("/api/admin/lead-tracker/refresh-follow-ups", {}, {
    headers: authHeader(adminToken()),
  });
  return data;
};
export const deleteAdminLeadMember = async (id) => {
  await client.delete(`/api/admin/lead-tracker/team-members/${id}`, {
    headers: authHeader(adminToken()),
  });
};

export const listTeamInterestStatuses = async (team, token) => {
  const path = team === "CALL" ? "/api/call-team/interest-statuses" : "/api/chat-team/interest-statuses";
  const { data } = await client.get(path, { headers: authHeader(token) });
  return data;
};

export const listTeamHotelImages = async (team, token, city = "") => {
  const path = team === "CALL" ? "/api/call-team/hotel-images" : "/api/chat-team/hotel-images";
  const { data } = await client.get(path, {
    params: city ? { city } : {},
    headers: authHeader(token),
  });
  return data;
};

// View only - there is no team endpoint to change availability.
export const listTeamHotelAvailability = async (team, token, startDate, endDate) => {
  const path = team === "CALL" ? "/api/call-team/hotel-availability" : "/api/chat-team/hotel-availability";
  const { data } = await client.get(path, {
    params: { startDate, endDate, _: Date.now() },
    headers: authHeader(token),
  });
  return Array.isArray(data) ? data : [];
};

export const getTeamHotelImageFile = async (team, token, imageId) => {
  const path = team === "CALL"
    ? `/api/call-team/hotel-images/${imageId}/file`
    : `/api/chat-team/hotel-images/${imageId}/file`;
  const { data } = await client.get(path, {
    headers: authHeader(token),
    responseType: "blob",
  });
  return data;
};

export const getTeamHotelImageCopySourceFile = async (team, token, imageId) => {
  const path = team === "CALL"
    ? `/api/call-team/hotel-images/${imageId}/copy-source-file`
    : `/api/chat-team/hotel-images/${imageId}/copy-source-file`;
  const { data } = await client.get(path, {
    headers: authHeader(token),
    responseType: "blob",
  });
  return data;
};

export const getTeamHotelImageCopyFile = async (team, token, imageId) => {
  const path = team === "CALL"
    ? `/api/call-team/hotel-images/${imageId}/copy-file`
    : `/api/chat-team/hotel-images/${imageId}/copy-file`;
  const { data } = await client.get(path, {
    headers: authHeader(token),
    responseType: "blob",
  });
  return data;
};

export const listAdminInterestStatuses = async (team) => {
  const { data } = await client.get("/api/admin/lead-tracker/interest-statuses", {
    params: team ? { team } : {},
    headers: authHeader(adminToken()),
  });
  return data;
};

export const saveAdminInterestStatus = async (status) => {
  const token = adminToken();
  if (status.id) {
    const { data } = await client.patch(`/api/admin/lead-tracker/interest-statuses/${status.id}`, status, {
      headers: authHeader(token),
    });
    return data;
  }
  const { data } = await client.post("/api/admin/lead-tracker/interest-statuses", status, {
    headers: authHeader(token),
  });
  return data;
};

export const deleteAdminInterestStatus = async (id) => {
  await client.delete(`/api/admin/lead-tracker/interest-statuses/${id}`, {
    headers: authHeader(adminToken()),
  });
};
